import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FirstAid, ArrowUUpLeft, FileArrowUp } from "@phosphor-icons/react";
import { Section, Pill, err, fmtD } from "./hrUtils";
import { TONE_CLASSES } from "../assets/assetUtils";

const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const today = () => localIso(new Date());
const tomorrow = () => localIso(new Date(Date.now() + 86400000));

/** My HR: report a sick day (manager + HR told, payroll updated) and press "I'm back" so the return-to-work chat is booked. */
export function SicknessCard({ onChanged }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ start: today(), note: "" });
  const [back, setBack] = useState(today());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => API.get("/my/sickness").then((r) => setD(r.data)).catch(() => setD({ on_payroll: false, recent: [] })), []);
  useEffect(() => { load(); }, [load]);
  const report = async () => {
    if (!window.confirm(`Report that you're off sick from ${fmtD(f.start)}? Your manager and HR will be told.`)) return;
    setBusy(true);
    try { const r = await API.post("/my/sickness", f); toast.success(r.data.message, { duration: 8000 }); setF({ start: today(), note: "" }); load(); } catch (e) { err(e, "Couldn't record that"); } finally { setBusy(false); }
  };
  const imBack = async () => {
    setBusy(true);
    try { const r = await API.post(`/my/sickness/${d.open.id}/back`, { return_date: back }); toast.success(r.data.message, { duration: 8000 }); load(); } catch (e) { err(e, "Couldn't record your return"); } finally { setBusy(false); }
  };
  const uploadNote = async (a, file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await API.post(`/my/sickness/${a.id}/fit-note`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(r.data.message); load(); onChanged?.(); } catch (e) { err(e, "Couldn't upload the fit note"); } finally { setBusy(false); }
  };
  if (!d || !d.on_payroll) return null;
  const needsNote = [...(d.open ? [d.open] : []), ...d.recent].filter((a) => a.fit_note_due);
  return (
    <Section title="Sickness" icon={FirstAid} testid="my-hr-sick-card">
      {needsNote.length > 0 && (
        <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm space-y-2" data-testid="my-hr-fit-note-due">
          <p className="font-medium flex items-center gap-1"><FileArrowUp size={15} /> Fit note needed</p>
          <p className="text-xs text-muted-foreground">Sickness over 7 days needs a fit note from your GP. Take a photo or upload the PDF — it's filed to your HR documents and only you and HR can see it.</p>
          {needsNote.map((a) => (
            <label key={a.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`my-hr-fit-note-${a.id}`}>
              <span>Sickness from {fmtD(a.start)} ({a.calendar_days} days)</span>
              <input type="file" accept="image/*,application/pdf" disabled={busy} onChange={(e) => uploadNote(a, e.target.files?.[0])} className="text-xs" data-testid={`my-hr-fit-note-${a.id}-file`} />
            </label>
          ))}
        </div>
      )}
      {d.open ? (
        <div className="space-y-3" data-testid="my-hr-sick-open">
          <div className="flex flex-wrap items-center gap-2"><Pill tone={TONE_CLASSES.overdue} testid="my-hr-sick-open-pill">Off sick since {fmtD(d.open.start)}</Pill>{d.open.fit_note_doc_id && <Pill tone={TONE_CLASSES.ok} testid="my-hr-sick-fit-note-ok">Fit note attached</Pill>}{d.open.note && <span className="text-xs text-muted-foreground">"{d.open.note}"</span>}</div>
          <p className="text-xs text-muted-foreground">Get well soon. On your first day back press the button below — your return-to-work chat with your manager is booked for that day.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">First day back</Label><Input type="date" value={back} min={d.open.start} max={tomorrow()} onChange={(e) => setBack(e.target.value)} className="h-9" data-testid="my-hr-sick-back-date" /></div>
            <Button size="sm" disabled={busy} onClick={imBack} data-testid="my-hr-sick-back"><ArrowUUpLeft size={14} className="mr-1" /> I'm back at work</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">Off sick from</Label><Input type="date" value={f.start} max={today()} onChange={(e) => setF({ ...f, start: e.target.value })} className="h-9" data-testid="my-hr-sick-start" /></div>
            <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Reason (optional — seen by your manager and HR)" className="h-9 text-sm flex-1 min-w-[200px]" data-testid="my-hr-sick-note" />
            <Button size="sm" variant="outline" disabled={busy || !f.start} onClick={report} data-testid="my-hr-sick-report"><FirstAid size={14} className="mr-1" /> Report a sick day</Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Your manager and HR are told straight away and payroll records the dates. When you're back, press "I'm back at work" and a return-to-work chat is booked automatically.</p>
        </div>
      )}
      {d.recent.length > 0 && <ul className="mt-3 text-xs text-muted-foreground space-y-0.5" data-testid="my-hr-sick-recent">{d.recent.slice(0, 5).map((a) => <li key={a.id}>{fmtD(a.start)}{a.end !== a.start ? ` → ${fmtD(a.end)}` : ""}{a.note ? ` · ${a.note}` : ""}{a.fit_note_doc_id ? " · fit note ✓" : ""}</li>)}</ul>}
    </Section>
  );
}
